import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
  ChangeDetectorRef, Component, forwardRef, Input, OnDestroy, OnInit, ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { GetTagCategoriesOnlyGQL, TagCategoryOnlyFragment, TagType } from 'generated/graphql';

import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { getTagTypeIcon } from 'field-shared/field-helper-functions';
import { TagCategoryResponseRuleValue } from 'app/query-common/rule-interfaces/TagCategoryResponsesRuleField';

@Component({
  selector: 'app-fields-advanced-rule-input',
  templateUrl: './fields-advanced-rule-input.component.html',
  styleUrls: ['./fields-advanced-rule-input.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => FieldsAdvancedRuleInputComponent), multi: true },
  ]
})
export class FieldsAdvancedRuleInputComponent
  extends WithDisposableSubject() implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() targetType: 'EntityType' | 'EntityConnectionType' | 'CalendarEventType' = 'EntityType';
  @Input() campaignId: string;

  /** Disabled state of the input */
  @Input() disabled: boolean;

  isOpen = false;
  limit = 100;

  tagCategoryModels: TagCategoryOnlyFragment[] = [];
  selectedTagCategoryIds = [];
  get selectedTagCategories(): TagCategoryOnlyFragment[] {
    return this.tagCategoryModels.filter(t => this.selectedTagCategoryIds.includes(t.id));
  }

  /** Reference to trigger to open the select
   *  (Used to dynamically set initial width)
   */
  @ViewChild(CdkOverlayOrigin) trigger: CdkOverlayOrigin;

  /**
   *  Use observable to get the trigger width to avoid a
   *  ExpressionChangedAfterItHasBeenCheckedError
   */
  private _triggerWidth = new Subject<string>();

  /**
   *  Use observable to get the trigger width to avoid a
   *  ExpressionChangedAfterItHasBeenCheckedError
   */
  triggerWidth$ = this._triggerWidth.asObservable().pipe(
    takeUntil(this.disposer)
  );

  // For ControlValueAccessor interface
  public onChangeCallback: () => void;
  public onTouchedCallback: () => any;


  // ----------ControlValueAccessor Implementation----------
  @Input()
  get value(): TagCategoryResponseRuleValue {
    return {
      tag_category_ids: this.selectedTagCategoryIds
    };
  }

  set value(value: TagCategoryResponseRuleValue) {
    this.selectedTagCategoryIds = value?.tag_category_ids || [];
    this.handleDataChange();
  }

  writeValue(obj: TagCategoryResponseRuleValue): void {
    if (obj) {
      this.value = obj;
    } else { }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = () => fn(this.value);
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = () => fn(this.value);
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ----------END----------
  constructor(
    private _cd: ChangeDetectorRef,
    private _getTagCategoriesOnlyGQL: GetTagCategoriesOnlyGQL
    ) {
    super();
  }

  ngOnInit(): void {
    this._getTagCategoriesOnlyGQL.fetch({
      campaignId: this.campaignId,
      targetType: this.targetType
    }).subscribe({
      next: res => {
        this.tagCategoryModels = res.data.getTagCategories.nodes;
        this._cd.markForCheck();
      }
    });
   }

  showMoreClicked() {
    this.limit += 100;
    this._cd.markForCheck();
  }

  tagCategoryIdsMapped(tagCategoryIds: string[]): Array<{id: string}> {
    return tagCategoryIds.map(id => ({ id }));
  }

  selectedTagCategoryIdsChanged(event: Array<{id: string}>) {
    this.selectedTagCategoryIds = event.map(e => e.id);
    this.handleDataChange();
  }

  removeCategory(tagCategory: {id: string}) {
    this.selectedTagCategoryIds = (this.selectedTagCategoryIds || []).filter(id => id !== tagCategory.id);
    this.handleDataChange();
  }

  clearSelectedTagCategoryIdsClicked() {
    this.reset();
  }

  reset() {
    this.selectedTagCategoryIds = [];
    this.handleDataChange();
  }

  openOverlay() {
    const width = this.trigger?.elementRef?.nativeElement?.offsetWidth;
    this._triggerWidth.next(width ? width + 'px' : '100%');
    this.isOpen = true;
  }

  closeOverlay() {
    this.isOpen = false;
  }

  getTagIcon(tagType: TagType) {
    const iconClass = getTagTypeIcon(tagType);
    return iconClass ? iconClass : 'fa-tag';
  }

  private handleDataChange(): void {
    this._cd.markForCheck();
    if (this.onChangeCallback) {
      this.onChangeCallback();
    }
  }
}
